import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listPurchaseSuggestions, decidePurchaseSuggestion, confirmPurchaseSuggestion } from "@/lib/forecasting.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Check, X, Truck, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/forecasting/suggestions")({ component: SuggestionsPage });

type Status = "pending" | "accepted" | "ordered" | "completed" | "dismissed";

function fmtMoney(cents: number) { return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`; }

function SuggestionsPage() {
  const { tenantId, facilityId, customerId } = useScope();
  const listFn = useServerFn(listPurchaseSuggestions);
  const decideFn = useServerFn(decidePurchaseSuggestion);
  const confirmFn = useServerFn(confirmPurchaseSuggestion);
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>("pending");

  const { data, isLoading } = useQuery({
    queryKey: ["forecast-suggestions", tenantId, facilityId, customerId, status],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null, status } }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["forecast-suggestions"] });
    qc.invalidateQueries({ queryKey: ["forecast-overview"] });
    qc.invalidateQueries({ queryKey: ["asns"] });
  };

  const decide = useMutation({
    mutationFn: (vars: { id: string; status: "accepted" | "dismissed" }) =>
      decideFn({ data: { id: vars.id, tenantId: tenantId!, status: vars.status } }),
    onSuccess: (_d, vars) => {
      toast.success(vars.status === "accepted" ? "Moved to Accepted" : "Dismissed");
      invalidate();
    },
    onError: (e) => toast.error(String(e)),
  });

  const confirmOrder = useMutation({
    mutationFn: (vars: { id: string }) => confirmFn({ data: { id: vars.id, tenantId: tenantId! } }),
    onSuccess: (d) => {
      toast.success(`ASN ${d.asnReference} created`);
      invalidate();
    },
    onError: (e) => toast.error(String(e)),
  });

  const rows = data?.rows ?? [];
  const totalValue = rows.reduce((s: number, r: any) => s + (r.est_cost_cents ?? 0), 0);

  return (
    <>
      <PageHeader title="Purchase Order Suggestions" description="Accept → Confirm to create an ASN. Receiving the ASN closes the suggestion." />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Tabs value={status} onValueChange={(v) => setStatus(v as Status)}>
            <TabsList>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="accepted">Accepted</TabsTrigger>
              <TabsTrigger value="ordered">Ordered</TabsTrigger>
              <TabsTrigger value="completed">Completed</TabsTrigger>
              <TabsTrigger value="dismissed">Dismissed</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="text-sm text-muted-foreground">
            {rows.length} suggestion{rows.length === 1 ? "" : "s"} · est. {fmtMoney(totalValue)}
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">On hand</TableHead>
                    <TableHead className="text-right">ROP</TableHead>
                    <TableHead className="text-right">30d forecast</TableHead>
                    <TableHead className="text-right">Suggest qty</TableHead>
                    <TableHead className="text-right">Est. cost</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
                  {!isLoading && rows.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No {status} suggestions.</TableCell></TableRow>
                  )}
                  {rows.map((r: any) => {
                    const sku = r.skus as { sku_code: string; name: string } | null;
                    const asn = (r as unknown as { asn_id: string | null; asns: { reference: string; status: string } | null }).asns;
                    const asnId = (r as unknown as { asn_id: string | null }).asn_id;
                    const busy = decide.isPending || confirmOrder.isPending;
                    return (
                      <TableRow key={r.id}>
                        <TableCell>
                          <Link
                            to="/app/forecasting/sku/$skuId"
                            params={{ skuId: r.sku_id }}
                            className="block hover:underline"
                          >
                            <div className="font-mono text-xs">{sku?.sku_code ?? r.sku_id.slice(0, 8)}</div>
                            <div className="text-xs text-muted-foreground">{sku?.name ?? ""}</div>
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm">{r.supplier_name ?? <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.on_hand_qty}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{r.reorder_point}</TableCell>
                        <TableCell className="text-right tabular-nums">{Number(r.forecast_30d).toLocaleString()}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{r.suggested_qty}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtMoney(r.est_cost_cents)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[18rem] truncate" title={r.reason}>{r.reason}</TableCell>
                        <TableCell className="text-right">
                          {status === "pending" && (
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" disabled={busy} onClick={() => decide.mutate({ id: r.id, status: "accepted" })} title="Accept">
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" disabled={busy} onClick={() => decide.mutate({ id: r.id, status: "dismissed" })} title="Dismiss">
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                          {status === "accepted" && (
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" disabled={busy} onClick={() => confirmOrder.mutate({ id: r.id })} title="Confirm — creates ASN">
                                <Truck className="h-3.5 w-3.5" /> Confirm
                              </Button>
                              <Button size="sm" variant="ghost" disabled={busy} onClick={() => decide.mutate({ id: r.id, status: "dismissed" })} title="Dismiss">
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                          {(status === "ordered" || status === "completed") && (
                            <div className="flex justify-end items-center gap-2">
                              {asn && asnId ? (
                                <Button asChild size="sm" variant="outline">
                                  <Link to="/app/receiving/$asnId" params={{ asnId }}>
                                    {asn.reference} <ExternalLink className="h-3 w-3" />
                                  </Link>
                                </Button>
                              ) : (
                                <Badge variant="outline" className="text-xs capitalize">{r.status}</Badge>
                              )}
                            </div>
                          )}
                          {status === "dismissed" && (
                            <Badge variant="outline" className="text-xs capitalize">{r.status}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}